import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { QuickPeekResponse } from '@/services/api/interfaces/ITravelAssistantRepository';

interface Festival {
  title: string;
  description: string;
  longDescription: string;
  imageUrls: string[];
}

interface PlaceOfInterest {
  title: string;
  description: string;
  longDescription: string;
  imageUrls: string[];
}

interface PointerItem {
  title: string;
  value: string;
  icon?: string;
  tag?: string;
  subtitle?: string;
}

interface PointerSection {
  title: string;
  items: PointerItem[];
}

interface Overview {
  name: string;
  description: string;
  district: string;
  city: string;
  state: string;
  pincode: string;
  rating: string;
  reviews: string;
  type: string;
  imageUrls: string[];
  pointers: PointerSection[];
}

interface OverViewStore {
  overview: Overview;
  festivals: Festival[];
  placesOfInterest: PlaceOfInterest[];
  addFestival: (festival: Festival) => void;
  updateFestivalImages: (title: string, imageUrls: string[]) => void;
  addPlaceOfInterest: (place: PlaceOfInterest) => void;
  addOverview: (data: Partial<Overview>) => void;
  addPointer: (sectionTitle: string, pointer: PointerItem) => void;
  clearOverViewData: () => void;
}

const useOverViewStore = create<OverViewStore>()(
  persist(
    set => ({
      overview: {
        name: '',
        description: '',
        district: '',
        city: '',
        state: '',
        pincode: '',
        rating: '',
        reviews: '',
        type: '',
        imageUrls: [],
        pointers: [],
      },
      festivals: [],
      placesOfInterest: [],
      addFestival: festival =>
        set(state => ({
          festivals: [...state.festivals, festival],
        })),
      updateFestivalImages: (title, imageUrls) =>
        set(state => ({
          festivals: state.festivals.map(festival =>
            festival.title === title ? { ...festival, imageUrls } : festival
          ),
        })),
      addPlaceOfInterest: place =>
        set(state => ({
          placesOfInterest: [...state.placesOfInterest, place],
        })),
      addOverview: data =>
        set(state => ({
          overview: { ...state.overview, ...data },
        })),
      addPointer: (sectionTitle, pointer) =>
        set(state => {
          const existingSection = state.overview.pointers.find(
            section => section.title === sectionTitle
          );

          if (existingSection) {
            existingSection.items.push(pointer);
          } else {
            state.overview.pointers.push({
              title: sectionTitle,
              items: [pointer],
            });
          }

          return {
            overview: { ...state.overview },
          };
        }),
      clearOverViewData: () =>
        set(() => ({
          overview: {
            name: '',
            description: '',
            district: '',
            city: '',
            state: '',
            pincode: '',
            rating: '',
            reviews: '',
            type: '',
            imageUrls: [],
            pointers: [],
          },
          festivals: [],
          placesOfInterest: [],
        })),
    }),
    {
      name: 'overview-storage',
    }
  )
);

interface BestTimeToVisit {
  title: string;
  durations: {
    month: string;
    startDate?: string;
    endDate?: string;
    preferredTravelMode: string;
    notes?: string;
    costBudget: string;
  }[];
  preferredTravelMode: string;
  icon: string;
}

interface InfoPointer {
  title: string;
  representationType: 'list' | 'iconic';
  items: {
    fullfills: boolean;
    icon: string;
    title: string;
    list: { title: string; description: string }[];
  }[];
}
interface ModeOfTransport {
  title: string;
  description: string;
  icon: string;
  timings: string;
  cost: string;
}

interface QuickPeekStore {
  bestTimeToVisit: BestTimeToVisit;
  infoPointers: InfoPointer[];
  infoPointerIcons: InfoPointer[];
  travelPlanning: InfoPointer;
  modeOfTransport: ModeOfTransport[];
  quickPeekResponse: QuickPeekResponse | null; // Store full API response
  // Loading states for each API section
  isLoadingTravelPlanning: boolean;
  isLoadingCulturalInfo: boolean;
  isLoadingActivitiesFacilities: boolean;
  // Separate loading states for split travel planning endpoints
  isLoadingTravelPlanningOnly: boolean;
  isLoadingNavigationAccessibility: boolean;
  // Separate loading states for split cultural endpoints
  isLoadingCulturalSocial: boolean;
  isLoadingHistoricalSouvenirs: boolean;
  setBestTimeToVisit: (data: Partial<BestTimeToVisit>) => void;
  addInfoPointer: (info: InfoPointer) => void;
  addTravelPlanning: (info: InfoPointer) => void;
  addInfoPointerIcon: (info: InfoPointer) => void;
  clearQuickPeekData: () => void;
  addModeOfTransport: (mode: ModeOfTransport) => void;
  setQuickPeekResponse: (response: QuickPeekResponse | null) => void;
  setLoadingTravelPlanning: (isLoading: boolean) => void;
  setLoadingCulturalInfo: (isLoading: boolean) => void;
  setLoadingActivitiesFacilities: (isLoading: boolean) => void;
  setLoadingTravelPlanningOnly: (isLoading: boolean) => void;
  setLoadingNavigationAccessibility: (isLoading: boolean) => void;
  setLoadingCulturalSocial: (isLoading: boolean) => void;
  setLoadingHistoricalSouvenirs: (isLoading: boolean) => void;
}

const useQuickPeekStore = create<QuickPeekStore>()(
  persist(
    (set, get) => ({
      bestTimeToVisit: {
        title: '',
        durations: [],
        preferredTravelMode: '',
        icon: '',
      },
      infoPointerIcons: [],
      infoPointers: [],
      travelPlanning: { title: '', representationType: 'list', items: [] },
      modeOfTransport: [],
      quickPeekResponse: null,
      isLoadingTravelPlanning: false,
      isLoadingCulturalInfo: false,
      isLoadingActivitiesFacilities: false,
      isLoadingTravelPlanningOnly: false,
      isLoadingNavigationAccessibility: false,
      isLoadingCulturalSocial: false,
      isLoadingHistoricalSouvenirs: false,
      setBestTimeToVisit: data =>
        set(state => ({
          bestTimeToVisit: { ...state.bestTimeToVisit, ...data },
        })),
      addInfoPointer: info =>
        set(state => ({
          infoPointers: [...state.infoPointers, info],
        })),
      addInfoPointerIcon: info =>
        set(state => ({
          infoPointerIcons: [...state.infoPointerIcons, info],
        })),
      addTravelPlanning: info =>
        set(state => ({
          travelPlanning: { ...state.travelPlanning, ...info },
        })),
      addModeOfTransport: mode =>
        set(state => ({
          modeOfTransport: [...state.modeOfTransport, mode],
        })),
      setQuickPeekResponse: response => {
        const currentState = get();
        const currentResponse = currentState.quickPeekResponse;

        console.log('🏪 [Zustand Store] setQuickPeekResponse called:', {
          hasResponse: !!response,
          responseType: typeof response,
          responseKeys: response ? Object.keys(response) : [],
          hasCurrentResponse: !!currentResponse,
          currentResponseKeys: currentResponse
            ? Object.keys(currentResponse)
            : [],
          // Log specific fields to track what's being merged
          hasTravelModes: !!response?.travelModes,
          travelModesCount: response?.travelModes?.length || 0,
          hasCulturalAspects: !!response?.culturalAspects,
          hasActivities: !!response?.activities,
          hasHistoricalSignificance: !!response?.historicalSignificance,
          hasFoodSpecialties: !!response?.foodSpecialties,
          timestamp: new Date().toISOString(),
        });

        set(state => {
          // Merge with existing response to preserve all data
          const mergedResponse = response
            ? {
                ...(state.quickPeekResponse || {}),
                ...response,
              }
            : response;

          const newState = {
            quickPeekResponse: mergedResponse,
          };

          console.log('🏪 [Zustand Store] ✅ State updated:', {
            hasQuickPeekResponse: !!newState.quickPeekResponse,
            quickPeekResponseKeys: newState.quickPeekResponse
              ? Object.keys(newState.quickPeekResponse)
              : [],
            // Log merged fields
            mergedTravelModesCount:
              newState.quickPeekResponse?.travelModes?.length || 0,
            mergedHasCulturalAspects:
              !!newState.quickPeekResponse?.culturalAspects,
            mergedHasActivities: !!newState.quickPeekResponse?.activities,
            mergedHasHistoricalSignificance:
              !!newState.quickPeekResponse?.historicalSignificance,
            mergedHasFoodSpecialties:
              !!newState.quickPeekResponse?.foodSpecialties,
            timestamp: new Date().toISOString(),
          });

          return newState;
        });
      },
      clearQuickPeekData: () =>
        set(() => ({
          bestTimeToVisit: {
            title: '',
            durations: [],
            preferredTravelMode: '',
            icon: '',
          },
          infoPointers: [],
          infoPointerIcons: [],
          modeOfTransport: [],
          travelPlanning: { title: '', representationType: 'list', items: [] },
          quickPeekResponse: null,
          isLoadingTravelPlanning: false,
          isLoadingCulturalInfo: false,
          isLoadingActivitiesFacilities: false,
          isLoadingTravelPlanningOnly: false,
          isLoadingNavigationAccessibility: false,
          isLoadingCulturalSocial: false,
          isLoadingHistoricalSouvenirs: false,
        })),
      setLoadingTravelPlanning: isLoading =>
        set(() => ({
          isLoadingTravelPlanning: isLoading,
        })),
      setLoadingCulturalInfo: isLoading =>
        set(() => ({
          isLoadingCulturalInfo: isLoading,
        })),
      setLoadingActivitiesFacilities: isLoading =>
        set(() => ({
          isLoadingActivitiesFacilities: isLoading,
        })),
      setLoadingTravelPlanningOnly: isLoading =>
        set(() => ({
          isLoadingTravelPlanningOnly: isLoading,
        })),
      setLoadingNavigationAccessibility: isLoading =>
        set(() => ({
          isLoadingNavigationAccessibility: isLoading,
        })),
      setLoadingCulturalSocial: isLoading =>
        set(() => ({
          isLoadingCulturalSocial: isLoading,
        })),
      setLoadingHistoricalSouvenirs: isLoading =>
        set(() => ({
          isLoadingHistoricalSouvenirs: isLoading,
        })),
    }),
    {
      name: 'quickpeek-storage',
    }
  )
);

interface OverviewImagesStore {
  images: string[];
  setImages: (images: string[]) => void;
  clearImages: () => void;
  activeImage: boolean;
  setActiveImage: (isActive: boolean) => void;
}

const useOverviewImagesStore = create<OverviewImagesStore>()(
  persist(
    (set, get) => ({
      images: [],
      activeImage: false,

      setImages: newImages => {
        const hasOldImages = get().images.length > 0;

        if (hasOldImages) {
          // Clear old state first
          set({ images: [], activeImage: false });
        }

        // Then set the new array
        set({ images: newImages });
      },

      clearImages: () =>
        set({
          images: [],
          activeImage: false,
        }),

      setActiveImage: isActive =>
        set({
          activeImage: isActive,
        }),
    }),
    {
      name: 'overview-images-storage', // localStorage key
    }
  )
);

// Add a utility function to clear all data
const clearAllData = () => {
  const clearOverViewData = useOverViewStore.getState().clearOverViewData;
  const clearQuickPeekData = useQuickPeekStore.getState().clearQuickPeekData;
  const clearImages = useOverviewImagesStore.getState().clearImages;

  // Call each store's clear method
  clearOverViewData();
  clearQuickPeekData();
  clearImages();
};

export {
  useOverViewStore,
  useQuickPeekStore,
  useOverviewImagesStore,
  clearAllData,
};
